/* eslint-disable @typescript-eslint/no-explicit-any */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useRef } from 'react';
import { useTable, useSortBy, useExpanded, useRowState, usePagination, } from 'react-table';
import styled from 'styled-components';
import { TableVariant } from './TableVariant';
import { CardListVariant } from './CardListVariant';
import { getSortBy } from './utils';
import { PaginationBase } from '../Pagination';
import { useMediaSize } from '../tools/utils';
const drawerSet = new WeakSet();
const StyledPaginationBase = styled(PaginationBase) `
  ${({ theme: { space } }) => `
    padding-top: ${space.xl};
  `}
`;
const getSubRows = (row) => {
    if (drawerSet.has(row))
        return [];
    const drawer = Object.assign({}, row);
    drawerSet.add(drawer);
    return [drawer];
};
// Convert the card list display option to the equivalent table display option in Pascal case
const toTableDisplayOption = (cardListDisplayOption) => {
    const displayOptionsMapping = {
        'sm-down': 'MdUp',
        'md-down': 'LgUp',
        none: 'XlDown',
    };
    return displayOptionsMapping[cardListDisplayOption];
};
// Modify the shared props so that loading for the component
// and load more buttons are always false if component is not visible
const getModifiedSharedProps = (componentDisplayed, originalSharedProps) => {
    var _a, _b;
    return Object.assign(Object.assign({}, originalSharedProps), { 
        // Pass 'isLoading' = true only when the component is actually
        // displayed so we don't duplicate aria-live announcements.
        isLoading: componentDisplayed && originalSharedProps.isLoading, loadMoreValues: originalSharedProps.loadMoreValues
            ? Object.assign(Object.assign({}, originalSharedProps.loadMoreValues), { loadingState: componentDisplayed &&
                    ((_a = originalSharedProps.loadMoreValues) === null || _a === void 0 ? void 0 : _a.loadingState)
                    ? (_b = originalSharedProps.loadMoreValues) === null || _b === void 0 ? void 0 : _b.loadingState
                    : 'loaded' }) : undefined });
};
const TableRefForwarding = (_a, ref) => {
    var _b;
    var { config: { data, columns, autoResetExpanded = true, autoResetSortBy = true }, table = {}, cardList, shared } = _a, others = __rest(_a, ["config", "table", "cardList", "shared"]);
    const memoizedColumns = useMemo(() => columns, [columns]);
    const memoizedData = useMemo(() => data, [data]);
    const tableRef = useRef(null);
    const tableCaptionRef = useRef(null);
    const cardListRef = useRef(null);
    const cardListHeadingRef = useRef(null);
    const mounted = useRef(false);
    const initialState = useMemo(() => {
        var _a;
        const paginationConditionalState = !shared.pagination
            ? {}
            : {
                pageSize: shared.pagination.resultsPerPage
                    ? (_a = shared.pagination.resultsPerPage) === null || _a === void 0 ? void 0 : _a.initialSelectedOption
                    : shared.pagination.pageSize || 10,
                // 10 is default page size
                pageIndex: 0,
            };
        return Object.assign(Object.assign({}, paginationConditionalState), getSortBy(columns));
    }, [columns]);
    const paginationConfigs = shared.pagination
        ? {
            autoResetPage: false,
            manualPagination: false,
            paginateExpandedRows: false,
        }
        : {};
    const tableInstance = useTable(Object.assign(Object.assign({ columns: memoizedColumns, data: memoizedData, initialState, disableMultiSort: true, disableSortRemove: true, autoResetSortBy,
        autoResetExpanded }, paginationConfigs), (!!(table === null || table === void 0 ? void 0 : table.drawer) && { getSubRows })), useSortBy, useExpanded, useRowState, ...(shared.pagination ? [usePagination] : []));
    // We determine if the table or card list is shown so we can prevent
    // setting a loading state on the hidden component and avoid duplicate
    // aria announcements.
    const displayTableVariant = useMediaSize(toTableDisplayOption(shared.displayCardList || 'none'));
    useEffect(() => {
        if (typeof ref === 'function') {
            ref(displayTableVariant ? tableRef.current : cardListRef.current);
        }
        else if (ref)
            ref.current = displayTableVariant ? tableRef.current : cardListRef.current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayTableVariant, tableRef, cardListRef]);
    const focusDescription = () => {
        var _a;
        return (_a = (displayTableVariant
            ? tableCaptionRef
            : cardListHeadingRef).current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const handlePageChange = (page) => {
        // React table uses 0 based array, but PaginationBase returns actual page number
        tableInstance.gotoPage(page - 1);
    };
    const handleResultsPerPageChange = (pageSize) => {
        tableInstance.setPageSize(pageSize);
        tableInstance.gotoPage(0);
    };
    const { pageIndex, pageSize } = tableInstance.state;
    // Focus description using useEffect instead of handlePageChange/handleResultsPerPageChange
    // because it fixes a timing issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (mounted.current) {
            focusDescription();
        }
    }, [pageIndex, pageSize]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        !displayTableVariant && cardList ? (React.createElement(CardListVariant, Object.assign({ ref: cardListRef, headingRef: cardListHeadingRef, tableInstance: tableInstance, cardList: cardList, shared: getModifiedSharedProps(!displayTableVariant, shared) }, others))) : (React.createElement(TableVariant, Object.assign({ ref: tableRef, captionRef: tableCaptionRef, tableInstance: tableInstance, table: table, shared: getModifiedSharedProps(displayTableVariant, shared) }, others))),
        shared.pagination && (React.createElement(StyledPaginationBase
        // React table uses 0 based array, but PaginationBase uses actual page number
        , { 
            // React table uses 0 based array, but PaginationBase uses actual page number
            currentPage: tableInstance.state.pageIndex + 1, onChange: handlePageChange, pageCount: tableInstance.pageCount, paginationName: shared.captionText, optionCount: shared.pagination.optionsCount || 9, resultsPerPage: (((_b = shared.pagination) === null || _b === void 0 ? void 0 : _b.resultsPerPage) && {
                onChange: handleResultsPerPageChange,
                options: shared.pagination.resultsPerPage.options,
                selected: tableInstance.state.pageSize,
            }) ||
                undefined }))));
};
// You cannot easily pass generics to forwardRefs, hence the casting here
// (https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref)
const Table = React.forwardRef(TableRefForwarding);
export { Table };
//# sourceMappingURL=Table.js.map